<template>
  <Loading v-if="loading" />
  <div class="options">
    <FormKit
      id="options-form"
      v-model="formData"
      type="form"
      :actions="false"
      @submit="submitHandler"
    >
      <ShowSettings
        :value="formData.show"
        :selected-position="formData.position"
        @update:selected-position="updateSelectedPosition"
        @select-option="selectShowSettings"
      />
      <TitleOptions :title="$t('titleUpperCase')" />
      <div class="options__translations">
        <FormKit type="group" name="title_text">
          <div
            v-for="(translation, index) in translations"
            :key="index"
            class="container"
          >
            <span>{{ translation.label }}</span>
            <Input
              type="text"
              :name="translation.value"
              :value="formData.title_text[translation.value]"
            />
          </div>
        </FormKit>
      </div>
      <OptionsInputs :options="formData.options" :user="user" />
      <AddOption
        :options="formData.options"
        :number-active-options="numberActiveOptions"
      />
      <TitleOptions :title="$t('multipleOptionsUpperCase')" />
      <div class="options__multipleOptions">
        <FormKit type="group" name="multiple_options">
          <Checkbox
            class="checkbox-multipleOptions"
            name="on"
            :title="$t('multipleOptions')"
            :value="formData.multiple_options.on"
            @change="
              formData.multiple_options.on = !formData.multiple_options.on
            "
          />
          <div
            v-if="formData.multiple_options.on"
            class="options__multipleOptions-timeout"
          >
            <p class="timeout-title">{{ $t('timeout') }}</p>
            <Input
              type="number"
              name="timeout"
              :value="formData.multiple_options.timeout"
              :step="0.1"
              required
            />
          </div>
        </FormKit>
      </div>
    </FormKit>
    <Button
      :variant="disableSubmitForm ? 'disabled' : 'filled'"
      :title="$t('save')"
      :disabled="disableSubmitForm"
      @click="submitForm"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import OptionsInputs from './OptionsInputs/OptionsInputs.vue';
import AddOption from './AddOption/AddOption.vue';

import { translations } from '../configs';
import { OptionsData, OptionsScreen } from '@/store/modules/pharmacy/types';
import { mapScreensToSelectedPositions } from '@/components/UI/ShowSettings/configs';

@Options({
  name: 'Options',
  components: {
    Loading,
    OptionsInputs,
    AddOption
  },
  data() {
    return {
      loading: false,
      formData: {
        title_text: {
          fi: '',
          sv: '',
          en: ''
        },
        options: {},
        multiple_options: {
          on: false,
          timeout: 1.5
        },
        show: false,
        position: 1
      },
      payload: {},
      positionNotSelected: false,
      disableSubmitForm: false,
      translations,
      numberActiveOptions: 0
    };
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy']),
    ...mapState('account', ['user'])
  },
  created() {
    if (this.pharmacy) {
      this.show();
    }
  },
  methods: {
    ...mapActions('pharmacy', ['updateFeedbackTemplate']),
    show() {
      this.loading = true;

      this.formData = {
        ...this.pharmacy.feedbackTemplate.options_screen,
        options: this.pharmacy.feedbackTemplate.options_screen.options
      };

      this.loading = false;
    },
    updateSelectedPosition(position: number) {
      this.formData.position = position;
    },
    selectShowSettings() {
      this.showSettings = !this.showSettings;
    },
    // eslint-disable-next-line
    filteredOptions(data: any) {
      const dataOptions = data.options;

      for (const key in dataOptions) {
        Object.keys(dataOptions[key]).forEach((innerKey) => {
          if (innerKey.startsWith('enabled_')) {
            delete dataOptions[key][innerKey];
          }
        });
      }

      let activeOptions: OptionsData = {};

      if (data.newOption) {
        activeOptions = dataOptions;

        const title: string = [data.newOption.title]
          .join('')
          .toLowerCase()
          .replace(/ |-/g, '_');

        const options = {
          ...activeOptions,
          [title]: {
            enabled: data.newOption.enabled,
            position: data.newOption.position,
            translations: data.newOption.translations
          }
        };

        return options;
      } else {
        activeOptions = data.options;
      }

      return activeOptions;
    },
    async submitHandler(data: OptionsScreen) {
      if (this.positionNotSelected) {
        this.payload = {
          grade_screen: {
            ...data
          }
        };
      } else {
        if (this.pharmacy) {
          const screens = this.pharmacy.feedbackTemplate;
          const name = this.$route.name.toLowerCase();

          const selectedPositions = mapScreensToSelectedPositions(
            screens,
            name
          );

          this.payload = {
            grade_screen: {
              ...data,
              position: selectedPositions.value
            }
          };
        }
      }

      if (!data.multiple_options.on) {
        this.payload = {
          options_screen: {
            ...data,
            options: this.filteredOptions(data)
          }
        };

        delete this.payload.options_screen.multiple_options.timeout;
      } else {
        this.payload = {
          options_screen: {
            ...data,
            options: this.filteredOptions(data),
            multiple_options: {
              ...data.multiple_options,
              timeout: +data.multiple_options.timeout
            }
          }
        };
      }

      delete this.payload.options_screen.newOption;

      this.loading = true;

      const { id } = this.$route.params;
      await this.updateFeedbackTemplate({ data: this.payload, pharmacyId: id });

      this.loading = false;
    },
    cleanForm() {
      this.$formkit.reset('options-form');
    },
    submitForm() {
      this.$formkit.submit('options-form');
    }
  },
  watch: {
    formData() {
      this.numberActiveOptions = Object.values(this.formData.options).filter(
        // eslint-disable-next-line
        (item: any) => item.enabled
      ).length;

      if (this.numberActiveOptions === 0) {
        if (!this.formData.newOption) {
          this.disableSubmitForm = true;
        } else {
          if (!this.formData.newOption?.enabled) {
            this.disableSubmitForm = true;
          } else {
            this.disableSubmitForm = false;
          }
        }
      } else {
        this.disableSubmitForm = false;
      }
    },
    pharmacy() {
      if (this.pharmacy) {
        this.show();
      }
    }
  }
})
export default class OptionsComponent extends Vue {}
</script>

<style lang="scss">
.options {
  padding: 24px;
  .formkit-form {
    .options__translations {
      width: 450px;
      .container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 30px;
        &:last-child {
          margin-bottom: 0;
        }
        .input {
          display: inline-block;
          width: 350px;
          margin-left: 15px;
          margin-bottom: 0;
        }
      }
    }
    .options__multipleOptions {
      margin-bottom: 32px;
      .checkbox-multipleOptions {
        margin-bottom: 32px;
      }
      .options__multipleOptions-timeout {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        .timeout-title {
          font-size: 16px;
          margin-right: 15px;
        }
        .input {
          height: 30px;
          width: 100px;
          margin-bottom: 0;
          .formkit-input {
            height: 30px;
          }
        }
      }
    }
  }
}
</style>
